import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Scale, ExternalLink, FileText, AlertTriangle, Bell, Search, 
  BookOpen, Globe, Building2, Plane, CheckCircle, Clock, Star,
  Link2, Calendar, ArrowUpRight, Filter, RefreshCw,
  Plus, Eye, Bookmark, BookmarkCheck, Flag, ChevronRight,
  Settings, Pencil, Trash2, Save, X, Database, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  Scale, FileText, AlertTriangle, Bell, BookOpen, Globe, Building2, Plane, 
  CheckCircle, Clock, Star, Link2, Calendar, ArrowUpRight, Filter, RefreshCw
};

const getIcon = (iconName) => ICON_MAP[iconName] || FileText;

// Default authorities (used as fallback if DB is empty)
const DEFAULT_AUTHORITIES = [
  {
    id: 'default-gaca',
    code: 'gaca',
    name: 'GACA',
    full_name: 'General Authority of Civil Aviation',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    website: 'https://www.gaca.gov.sa/en',
    color: 'emerald',
    resources: [
      { type: 'regulations', label: 'GACAR Regulations', url: 'https://www.gaca.gov.sa/en/eServices/Laws-And-Regulations', icon: 'Scale' },
      { type: 'circulars', label: 'Circulars & AIC', url: 'https://www.gaca.gov.sa/en/eServices/Circulars', icon: 'FileText' },
    ]
  }
];

// Sample tracked updates
const SAMPLE_UPDATES = [
  {
    id: '1',
    authority: 'gaca',
    type: 'circular',
    reference: 'GACA-CIR-2025-012',
    title: 'Updated SMS Implementation Requirements',
    summary: 'New requirements for Safety Management System documentation and reporting frequency for AOC holders.',
    published_date: '2025-02-10',
    effective_date: '2025-04-01',
    priority: 'high',
    status: 'action_required',
    impact_areas: ['SMS', 'Documentation', 'Reporting'],
    url: 'https://www.gaca.gov.sa/en/eServices/Circulars'
  },
  {
    id: '2',
    authority: 'easa',
    type: 'sib',
    reference: 'EASA SIB 2025-02',
    title: 'Lithium Battery Fire Prevention',
    summary: 'Safety recommendations regarding lithium battery transportation and handling procedures.',
    published_date: '2025-02-05',
    effective_date: '2025-02-05',
    priority: 'urgent',
    status: 'reviewed',
    impact_areas: ['Cargo', 'Ground Handling', 'Training'],
    url: 'https://www.easa.europa.eu/en/safety-publications'
  }
];

const PRIORITY_STYLES = {
  urgent: { bg: 'bg-red-600', text: 'text-red-400', border: 'border-red-500' },
  high: { bg: 'bg-orange-600', text: 'text-orange-400', border: 'border-orange-500' },
  normal: { bg: 'bg-blue-600', text: 'text-blue-400', border: 'border-blue-500' },
  info: { bg: 'bg-slate-600', text: 'text-slate-400', border: 'border-slate-500' }
};

const STATUS_STYLES = {
  action_required: { bg: 'bg-red-900/30', text: 'text-red-400', label: 'Action Required' },
  pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', label: 'Pending Review' },
  reviewed: { bg: 'bg-blue-900/30', text: 'text-blue-400', label: 'Reviewed' },
  noted: { bg: 'bg-slate-800', text: 'text-slate-400', label: 'Noted' },
  completed: { bg: 'bg-emerald-900/30', text: 'text-emerald-400', label: 'Completed' }
};

const COLOR_STYLES = {
  emerald: 'from-emerald-600/20 to-emerald-900/10 border-emerald-500/30 hover:border-emerald-500/50',
  blue: 'from-blue-600/20 to-blue-900/10 border-blue-500/30 hover:border-blue-500/50',
  red: 'from-red-600/20 to-red-900/10 border-red-500/30 hover:border-red-500/50',
  sky: 'from-sky-600/20 to-sky-900/10 border-sky-500/30 hover:border-sky-500/50',
  purple: 'from-purple-600/20 to-purple-900/10 border-purple-500/30 hover:border-purple-500/50',
  amber: 'from-amber-600/20 to-amber-900/10 border-amber-500/30 hover:border-amber-500/50',
  rose: 'from-rose-600/20 to-rose-900/10 border-rose-500/30 hover:border-rose-500/50'
};

// Authority Card Component
const AuthorityCard = ({ authority, isAdmin, onEdit }) => {
  const colorStyle = COLOR_STYLES[authority.color] || COLOR_STYLES.blue;

  return (
    <Card className={cn("bg-gradient-to-br border transition-all", colorStyle)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{authority.flag}</span>
            <div>
              <CardTitle className="text-white text-lg">{authority.name}</CardTitle>
              <CardDescription className="text-xs">{authority.country}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-400 hover:text-white"
                onClick={() => onEdit(authority)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-400 hover:text-white"
              onClick={() => window.open(authority.website, '_blank')}
            >
              <Globe className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <p className="text-sm text-slate-400 mb-3">{authority.full_name}</p>
        <div className="flex flex-wrap gap-2">
          {authority.resources?.map((resource, idx) => {
            const Icon = getIcon(resource.icon);
            return (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs"
                onClick={() => window.open(resource.url, '_blank')}
              >
                <Icon className="w-3 h-3 mr-1" />
                {resource.label}
                <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Update Card Component
const UpdateCard = ({ update, authorities, onStatusChange, onBookmark, isBookmarked }) => {
  const authority = authorities.find(a => a.code === update.authority);
  const priorityStyle = PRIORITY_STYLES[update.priority] || PRIORITY_STYLES.normal;
  const statusStyle = STATUS_STYLES[update.status] || STATUS_STYLES.noted;

  return (
    <Card className={cn(
      "bg-slate-900/60 border-slate-800 transition-all hover:border-slate-700",
      update.priority === 'urgent' && "border-l-4 border-l-red-500",
      update.priority === 'high' && "border-l-4 border-l-orange-500"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-lg">{authority?.flag || '📋'}</span>
              <Badge variant="outline" className="text-xs border-slate-700">
                {authority?.name || update.authority}
              </Badge>
              <Badge className={cn("text-xs", priorityStyle.bg)}>
                {update.priority}
              </Badge>
              <Badge className={cn("text-xs", statusStyle.bg, statusStyle.text)}>
                {statusStyle.label}
              </Badge>
              <span className="font-mono text-xs text-slate-500">{update.reference}</span>
            </div>
            
            <h3 className="text-white font-medium mb-1">{update.title}</h3>
            <p className="text-slate-400 text-sm mb-3">{update.summary}</p>
            
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Published: {format(new Date(update.published_date), 'PP')}
              </span>
              {update.effective_date && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Effective: {format(new Date(update.effective_date), 'PP')}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-1 mt-2">
              {update.impact_areas?.map(area => (
                <Badge key={area} variant="outline" className="text-xs border-slate-700 text-slate-400">
                  {area}
                </Badge>
              ))}
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button
              variant="ghost"
              size="sm"
              className={isBookmarked ? "text-yellow-400" : "text-slate-400"}
              onClick={() => onBookmark(update.id)}
            >
              {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
              onClick={() => window.open(update.url, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
          <Select value={update.status} onValueChange={(value) => onStatusChange(update.id, value)}>
            <SelectTrigger className="w-[160px] h-8 bg-slate-950 border-slate-800 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="action_required">Action Required</SelectItem>
              <SelectItem value="pending">Pending Review</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="noted">Noted</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 text-xs"
            onClick={() => window.open(update.url, '_blank')}
          >
            View Document
            <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Edit Authority Dialog
const EditAuthorityDialog = ({ authority, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState(authority || {});
  const [resources, setResources] = useState(authority?.resources || []);

  useEffect(() => {
    if (authority) {
      setFormData(authority);
      setResources(authority.resources || []);
    }
  }, [authority]);

  const handleAddResource = () => {
    setResources([...resources, { type: '', label: '', url: '', icon: 'FileText' }]);
  };

  const handleRemoveResource = (index) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const handleResourceChange = (index, field, value) => {
    const updated = [...resources];
    updated[index] = { ...updated[index], [field]: value };
    setResources(updated);
  };

  const handleSave = () => {
    onSave({ ...formData, resources });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {authority?.id ? 'Edit Regulatory Authority' : 'Add Regulatory Authority'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Code</Label>
              <Input 
                value={formData.code || ''}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="bg-slate-950 border-slate-800"
                placeholder="e.g., gaca"
              />
            </div>
            <div>
              <Label className="text-slate-300">Short Name</Label>
              <Input 
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-slate-950 border-slate-800"
                placeholder="e.g., GACA"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-slate-300">Full Name</Label>
            <Input 
              value={formData.full_name || ''}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              className="bg-slate-950 border-slate-800"
              placeholder="e.g., General Authority of Civil Aviation"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300">Country</Label>
              <Input 
                value={formData.country || ''}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                className="bg-slate-950 border-slate-800"
              />
            </div>
            <div>
              <Label className="text-slate-300">Flag Emoji</Label>
              <Input 
                value={formData.flag || ''}
                onChange={(e) => setFormData({...formData, flag: e.target.value})}
                className="bg-slate-950 border-slate-800"
                placeholder="🇸🇦"
              />
            </div>
            <div>
              <Label className="text-slate-300">Color</Label>
              <Select value={formData.color || 'blue'} onValueChange={(v) => setFormData({...formData, color: v})}>
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="emerald">Emerald</SelectItem>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                  <SelectItem value="sky">Sky</SelectItem>
                  <SelectItem value="purple">Purple</SelectItem>
                  <SelectItem value="amber">Amber</SelectItem>
                  <SelectItem value="rose">Rose</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label className="text-slate-300">Website URL</Label>
            <Input 
              value={formData.website || ''}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              className="bg-slate-950 border-slate-800"
              placeholder="https://..."
            />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Resource Links</Label>
              <Button variant="outline" size="sm" onClick={handleAddResource} className="border-slate-700">
                <Plus className="w-3 h-3 mr-1" /> Add Link
              </Button>
            </div>
            
            {resources.map((resource, index) => (
              <div key={index} className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Resource {index + 1}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                    onClick={() => handleRemoveResource(index)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={resource.label || ''}
                    onChange={(e) => handleResourceChange(index, 'label', e.target.value)}
                    className="bg-slate-900 border-slate-700 text-sm"
                    placeholder="Label (e.g., Regulations)"
                  />
                  <Select 
                    value={resource.icon || 'FileText'} 
                    onValueChange={(v) => handleResourceChange(index, 'icon', v)}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="Scale">Scale (Regulations)</SelectItem>
                      <SelectItem value="FileText">FileText (Documents)</SelectItem>
                      <SelectItem value="AlertTriangle">AlertTriangle (Safety)</SelectItem>
                      <SelectItem value="Bell">Bell (News)</SelectItem>
                      <SelectItem value="BookOpen">BookOpen (Guidance)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={resource.url || ''}
                  onChange={(e) => handleResourceChange(index, 'url', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-sm"
                  placeholder="URL (https://...)"
                />
              </div>
            ))}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function RegulatoryResourcesPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('authorities');
  const [authorities, setAuthorities] = useState([]);
  const [updates, setUpdates] = useState(SAMPLE_UPDATES);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsSeeding, setNeedsSeeding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAuthority, setFilterAuthority] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAuthority, setEditingAuthority] = useState(null);
  
  // Add update dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUpdate, setNewUpdate] = useState({
    authority: '',
    type: 'circular',
    reference: '',
    title: '',
    summary: '',
    published_date: '',
    effective_date: '',
    priority: 'normal',
    url: '',
    impact_areas: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchAuthorities();
    fetchUpdates();
  }, []);

  const fetchAuthorities = async () => {
    setLoading(true);
    try {
      const response = await api.get('/regulatory-authorities');
      if (response.data.authorities && response.data.authorities.length > 0) {
        setAuthorities(response.data.authorities);
        setNeedsSeeding(false);
      } else {
        setAuthorities(DEFAULT_AUTHORITIES);
        setNeedsSeeding(true);
      }
    } catch (error) {
      console.error('Error fetching authorities:', error);
      setAuthorities(DEFAULT_AUTHORITIES);
      setNeedsSeeding(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const response = await api.get('/regulatory-updates');
      if (response.data && response.data.length > 0) {
        setUpdates(response.data);
      }
    } catch (error) {
      console.log('Using sample updates');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await api.post('/regulatory-authorities/seed-defaults');
      toast.success('Default regulatory authorities loaded');
      fetchAuthorities();
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const handleEditAuthority = (authority) => {
    setEditingAuthority(authority);
    setEditDialogOpen(true);
  };

  const handleAddAuthority = () => {
    setEditingAuthority({ code: '', name: '', full_name: '', country: '', flag: '', website: '', color: 'blue', resources: [] });
    setEditDialogOpen(true);
  };

  const handleSaveAuthority = async (authorityData) => {
    try {
      if (editingAuthority?.id && !editingAuthority.id.startsWith('default-')) {
        // Update existing
        await api.put(`/regulatory-authorities/${editingAuthority.id}`, authorityData);
        toast.success('Authority updated');
      } else {
        // Create new
        await api.post('/regulatory-authorities', authorityData);
        toast.success('Authority created');
      }
      setEditDialogOpen(false);
      fetchAuthorities();
    } catch (error) {
      toast.error('Failed to save authority');
    }
  };

  const handleDeleteAuthority = async (authorityId) => {
    if (!confirm('Are you sure you want to delete this authority?')) return;
    
    try {
      await api.delete(`/regulatory-authorities/${authorityId}`);
      toast.success('Authority deleted');
      fetchAuthorities();
    } catch (error) {
      toast.error('Failed to delete authority');
    }
  };

  const handleStatusChange = async (updateId, newStatus) => {
    setUpdates(prev => prev.map(u => 
      u.id === updateId ? { ...u, status: newStatus } : u
    ));
    
    try {
      await api.put(`/regulatory-updates/${updateId}/status`, { status: newStatus });
      toast.success('Status updated');
    } catch (error) {
      // Optimistic update already applied
    }
  };

  const handleBookmark = (updateId) => {
    setBookmarkedIds(prev => 
      prev.includes(updateId) 
        ? prev.filter(id => id !== updateId)
        : [...prev, updateId]
    );
  };

  const handleAddUpdate = async () => {
    const update = {
      ...newUpdate,
      id: Date.now().toString(),
      status: 'pending',
      impact_areas: newUpdate.impact_areas.split(',').map(s => s.trim()).filter(Boolean)
    };
    
    setUpdates(prev => [update, ...prev]);
    setShowAddDialog(false);
    setNewUpdate({
      authority: '',
      type: 'circular',
      reference: '',
      title: '',
      summary: '',
      published_date: '',
      effective_date: '',
      priority: 'normal',
      url: '',
      impact_areas: ''
    });
    
    toast.success('Regulatory update added');
  };

  // Filter updates
  const filteredUpdates = updates.filter(update => {
    const matchesSearch = searchQuery === '' || 
      update.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      update.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      update.summary?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAuthority = filterAuthority === 'all' || update.authority === filterAuthority;
    const matchesPriority = filterPriority === 'all' || update.priority === filterPriority;
    const matchesStatus = filterStatus === 'all' || update.status === filterStatus;
    
    return matchesSearch && matchesAuthority && matchesPriority && matchesStatus;
  });

  // Stats
  const actionRequired = updates.filter(u => u.status === 'action_required').length;
  const pendingReview = updates.filter(u => u.status === 'pending').length;
  const urgentCount = updates.filter(u => u.priority === 'urgent').length;

  return (
    <div className="space-y-6" data-testid="regulatory-resources-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Scale className="w-6 h-6 text-emerald-400" />
            {t('regulatory.resourcesHub')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('regulatory.resourcesHubDesc')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-slate-700"
            onClick={() => { fetchAuthorities(); fetchUpdates(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="outline"
                className="border-slate-700"
                onClick={handleAddAuthority}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('regulatory.addAuthority')}
              </Button>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('regulatory.trackUpdate')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white">{t('regulatory.trackRegulatoryUpdate')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">{t('regulatory.authority')}</Label>
                        <Select value={newUpdate.authority} onValueChange={(v) => setNewUpdate(p => ({...p, authority: v}))}>
                          <SelectTrigger className="bg-slate-950 border-slate-800">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            {authorities.map(auth => (
                              <SelectItem key={auth.code} value={auth.code}>
                                {auth.flag} {auth.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">{t('common.type')}</Label>
                        <Select value={newUpdate.type} onValueChange={(v) => setNewUpdate(p => ({...p, type: v}))}>
                          <SelectTrigger className="bg-slate-950 border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="circular">{t('regulatory.circular')}</SelectItem>
                            <SelectItem value="ad">{t('regulatory.airworthinessDirective')}</SelectItem>
                            <SelectItem value="sib">{t('regulatory.safetyBulletin')}</SelectItem>
                            <SelectItem value="notice">{t('regulatory.notice')}</SelectItem>
                            <SelectItem value="amendment">{t('regulatory.amendment')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">{t('regulatory.referenceNumber')}</Label>
                      <Input 
                        value={newUpdate.reference}
                        onChange={(e) => setNewUpdate(p => ({...p, reference: e.target.value}))}
                        className="bg-slate-950 border-slate-800"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t('common.name')}</Label>
                      <Input 
                        value={newUpdate.title}
                        onChange={(e) => setNewUpdate(p => ({...p, title: e.target.value}))}
                        className="bg-slate-950 border-slate-800"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t('common.summary')}</Label>
                      <Textarea 
                        value={newUpdate.summary}
                        onChange={(e) => setNewUpdate(p => ({...p, summary: e.target.value}))}
                        className="bg-slate-950 border-slate-800"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">{t('regulatory.publishedDate')}</Label>
                        <Input 
                          type="date"
                          value={newUpdate.published_date}
                          onChange={(e) => setNewUpdate(p => ({...p, published_date: e.target.value}))}
                          className="bg-slate-950 border-slate-800"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">{t('common.priority')}</Label>
                        <Select value={newUpdate.priority} onValueChange={(v) => setNewUpdate(p => ({...p, priority: v}))}>
                          <SelectTrigger className="bg-slate-950 border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="urgent">{t('regulatory.urgent')}</SelectItem>
                            <SelectItem value="high">{t('common.high')}</SelectItem>
                            <SelectItem value="normal">{t('regulatory.normal')}</SelectItem>
                            <SelectItem value="info">{t('common.info')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">{t('regulatory.documentUrl')}</Label>
                      <Input 
                        value={newUpdate.url}
                        onChange={(e) => setNewUpdate(p => ({...p, url: e.target.value}))}
                        className="bg-slate-950 border-slate-800"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-700">
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleAddUpdate} className="bg-emerald-600 hover:bg-emerald-700">
                      {t('regulatory.addUpdate')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Needs Seeding Banner */}
      {needsSeeding && isAdmin && (
        <Card className="bg-amber-950/30 border-amber-600/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-amber-400" />
              <div>
                <h3 className="font-medium text-amber-300">{t('regulatory.databaseNotInitialized')}</h3>
                <p className="text-amber-200/70 text-sm">{t('regulatory.loadDefaultsDesc')}</p>
              </div>
            </div>
            <Button onClick={handleSeedDefaults} className="bg-amber-600 hover:bg-amber-700">
              <Database className="w-4 h-4 mr-2" />
              {t('regulatory.loadDefaults')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('regulatory.authorities')}</p>
              <p className="text-2xl font-bold text-white">{authorities.length}</p>
            </div>
            <Building2 className="w-8 h-8 text-slate-600" />
          </CardContent>
        </Card>
        <Card className={cn("bg-slate-900/60 border-slate-800", actionRequired > 0 && "border-l-4 border-l-red-500")}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('regulatory.actionRequired')}</p>
              <p className={cn("text-2xl font-bold", actionRequired > 0 ? "text-red-400" : "text-white")}>{actionRequired}</p>
            </div>
            <Flag className={cn("w-8 h-8", actionRequired > 0 ? "text-red-400" : "text-slate-600")} />
          </CardContent>
        </Card>
        <Card className={cn("bg-slate-900/60 border-slate-800", pendingReview > 0 && "border-l-4 border-l-yellow-500")}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('regulatory.pendingReview')}</p>
              <p className={cn("text-2xl font-bold", pendingReview > 0 ? "text-yellow-400" : "text-white")}>{pendingReview}</p>
            </div>
            <Clock className={cn("w-8 h-8", pendingReview > 0 ? "text-yellow-400" : "text-slate-600")} />
          </CardContent>
        </Card>
        <Card className={cn("bg-slate-900/60 border-slate-800", urgentCount > 0 && "border-l-4 border-l-orange-500")}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('regulatory.urgentItems')}</p>
              <p className={cn("text-2xl font-bold", urgentCount > 0 ? "text-orange-400" : "text-white")}>{urgentCount}</p>
            </div>
            <AlertTriangle className={cn("w-8 h-8", urgentCount > 0 ? "text-orange-400" : "text-slate-600")} />
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="authorities" className="data-[state=active]:bg-emerald-600">
            <Building2 className="w-4 h-4 mr-2" />
            {t('regulatory.regulatoryAuthorities')}
          </TabsTrigger>
          <TabsTrigger value="updates" className="data-[state=active]:bg-emerald-600">
            <Bell className="w-4 h-4 mr-2" />
            {t('regulatory.trackedUpdates')}
            {(actionRequired + pendingReview) > 0 && (
              <Badge className="ml-2 bg-red-600 text-white text-xs">{actionRequired + pendingReview}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bookmarks" className="data-[state=active]:bg-emerald-600">
            <Bookmark className="w-4 h-4 mr-2" />
            {t('regulatory.bookmarked')}
          </TabsTrigger>
        </TabsList>

        {/* Authorities Tab */}
        <TabsContent value="authorities" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {authorities.map((authority) => (
                <AuthorityCard 
                  key={authority.id || authority.code} 
                  authority={authority} 
                  isAdmin={isAdmin}
                  onEdit={handleEditAuthority}
                />
              ))}
            </div>
          )}
          
          {/* Quick Links */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-400" />
                {t('regulatory.quickReferenceLinks')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button
                  variant="outline"
                  className="border-slate-700 justify-start h-auto py-3"
                  onClick={() => window.open('https://www.icao.int/safety/SafetyManagement/Pages/default.aspx', '_blank')}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">ICAO SMM</p>
                    <p className="text-xs text-slate-400">{t('regulatory.safetyManagementManual')}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-700 justify-start h-auto py-3"
                  onClick={() => window.open('https://skybrary.aero/', '_blank')}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">SKYbrary</p>
                    <p className="text-xs text-slate-400">{t('regulatory.aviationSafetyKnowledge')}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-700 justify-start h-auto py-3"
                  onClick={() => window.open('https://asrs.arc.nasa.gov/', '_blank')}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">NASA ASRS</p>
                    <p className="text-xs text-slate-400">{t('regulatory.aviationSafetyReports')}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-700 justify-start h-auto py-3"
                  onClick={() => window.open('https://flightsafety.org/', '_blank')}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">FSF</p>
                    <p className="text-xs text-slate-400">{t('regulatory.flightSafetyFoundation')}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 ml-auto" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Updates Tab */}
        <TabsContent value="updates" className="space-y-4">
          {/* Filters */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder={t('regulatory.searchUpdates')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-slate-950 border-slate-800"
                    />
                  </div>
                </div>
                <Select value={filterAuthority} onValueChange={setFilterAuthority}>
                  <SelectTrigger className="w-[140px] bg-slate-950 border-slate-800">
                    <SelectValue placeholder={t('regulatory.authority')} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="all">{t('regulatory.allAuthorities')}</SelectItem>
                    {authorities.map(auth => (
                      <SelectItem key={auth.code} value={auth.code}>{auth.flag} {auth.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-[120px] bg-slate-950 border-slate-800">
                    <SelectValue placeholder={t('common.priority')} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="all">{t('regulatory.allPriorities')}</SelectItem>
                    <SelectItem value="urgent">{t('regulatory.urgent')}</SelectItem>
                    <SelectItem value="high">{t('common.high')}</SelectItem>
                    <SelectItem value="normal">{t('regulatory.normal')}</SelectItem>
                    <SelectItem value="info">{t('common.info')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800">
                    <SelectValue placeholder={t('common.status')} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="all">{t('regulatory.allStatuses')}</SelectItem>
                    <SelectItem value="action_required">{t('regulatory.actionRequired')}</SelectItem>
                    <SelectItem value="pending">{t('regulatory.pendingReview')}</SelectItem>
                    <SelectItem value="reviewed">{t('regulatory.reviewed')}</SelectItem>
                    <SelectItem value="noted">{t('regulatory.noted')}</SelectItem>
                    <SelectItem value="completed">{t('common.completed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Updates List */}
          <div className="space-y-4">
            {filteredUpdates.length === 0 ? (
              <Card className="bg-slate-900/60 border-slate-800">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">{t('regulatory.noUpdatesFound')}</p>
                </CardContent>
              </Card>
            ) : (
              filteredUpdates.map((update) => (
                <UpdateCard
                  key={update.id}
                  update={update}
                  authorities={authorities}
                  onStatusChange={handleStatusChange}
                  onBookmark={handleBookmark}
                  isBookmarked={bookmarkedIds.includes(update.id)}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Bookmarks Tab */}
        <TabsContent value="bookmarks" className="space-y-4">
          {bookmarkedIds.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-8 text-center">
                <Bookmark className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">{t('regulatory.noBookmarkedUpdates')}</p>
                <p className="text-slate-500 text-sm mt-2">{t('regulatory.clickBookmarkToSave')}</p>
              </CardContent>
            </Card>
          ) : (
            updates
              .filter(u => bookmarkedIds.includes(u.id))
              .map((update) => (
                <UpdateCard
                  key={update.id}
                  update={update}
                  authorities={authorities}
                  onStatusChange={handleStatusChange}
                  onBookmark={handleBookmark}
                  isBookmarked={true}
                />
              ))
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Authority Dialog */}
      <EditAuthorityDialog
        authority={editingAuthority}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleSaveAuthority}
      />
    </div>
  );
}
